import {
  LabelsAdapter,
  LabelsAdapterConfig,
  LabelsAdapterValue,
} from "common/labelsAdapter";
import { TranslateOptions } from "i18n-js";
import { WireAdapter } from "lwc";
import * as locales from "./locales";
import {
  configure,
  getLocale,
  registerLocaleChangeHandler,
} from "@devforce/trailhead-client-locale";

configure((i18n) => {
  // Use "en" labels as a fallback
  i18n.fallbacks = true;
  i18n.missingPlaceholder = () => {
    return undefined;
  };
});

export interface SharedLabelsAdapterValue {
  shared: LabelsAdapterValue;
  [k: string]: string | LabelsAdapterValue;
}

export interface SharedLabelsAdapterValueConfig {
  locales?: LabelsAdapterConfig["locales"];
  options?: {
    shared?: LabelsAdapterConfig["options"];
  } & LabelsAdapterConfig["options"];
}

export class SharedLabelsAdapter implements WireAdapter {
  private config: SharedLabelsAdapterValueConfig = undefined!;
  private localLabelAdapter: LabelsAdapter = undefined!;
  private sharedLabelAdapter: LabelsAdapter = undefined!;
  private sharedLabels: LabelsAdapterValue = undefined!;
  private localLabels: LabelsAdapterValue = {};
  private setValue: (value: SharedLabelsAdapterValue) => void;
  private removeLocaleChangeHandler = () => {};
  private locale: string = undefined!;

  constructor(setValue: (value: SharedLabelsAdapterValue) => void) {
    this.setValue = setValue;
  }

  connect() {
    this.removeLocaleChangeHandler = registerLocaleChangeHandler(() => {
      this.update(this.config);
    });
    this.sharedLabelAdapter = new LabelsAdapter((value: LabelsAdapterValue) => {
      this.sharedLabels = value;
      this.updateValue();
    });
    this.sharedLabelAdapter.connect();

    this.localLabelAdapter = new LabelsAdapter((value: LabelsAdapterValue) => {
      this.localLabels = value;
      this.updateValue();
    });
    this.localLabelAdapter.connect();
  }

  update(config: SharedLabelsAdapterValueConfig) {
    if (config === this.config && this.locale === getLocale()) return;

    this.locale = getLocale();
    this.config = config;
    const { local, shared } = getLabelOptions(config);
    this.sharedLabelAdapter.update({
      locales,
      options: shared,
    });
    this.localLabelAdapter.update({
      locales: this.config.locales ?? { en: {} },
      options: local,
    });
  }

  disconnect() {
    this.removeLocaleChangeHandler();
    this.sharedLabelAdapter.disconnect();
    this.localLabelAdapter.disconnect();
  }

  private updateValue() {
    this.setValue({
      shared: this.sharedLabels,
      ...this.localLabels,
    });
  }
}

function getLabelOptions(config: SharedLabelsAdapterValueConfig) {
  const result: {
    shared: Record<string, I18n.TranslateOptions>;
    local: Record<string, I18n.TranslateOptions>;
  } = {
    shared: {},
    local: {},
  };

  if (!config || !config.options) return result;

  for (let [key, value] of Object.entries(config.options)) {
    const [prefix, option] = key.split(".");

    if (key === "shared" && typeof value !== "string") {
      result.shared = {
        ...result.shared,
        ...value,
      };
    } else if (option && prefix === "shared") {
      result.shared[option] = value as TranslateOptions;
    } else {
      result.local[key] = value as TranslateOptions;
    }
  }

  return result;
}
